import { computed, inject, Injectable } from '@angular/core';
import type { ZegoSoundLevelInfo } from 'zego-express-engine-webrtc/sdk/code/zh/ZegoExpressEntity.web';

import { LocalMediaPermissionService } from '../../../services/local-media-permission.service';
import { ZegoMediaControlService } from '../../../services/zego-media-control.service';
import { ZegoStreamService } from '../../../services/zego-stream.service';
import { ZimService } from '../../../services/zim.service';
import { createLogger } from '../../../utils/logger.util';
import { getUserInfo } from '../../../utils/utils';
import { CALL_STATE, CALL_TYPE } from '../constants';
import { CallType } from '../types/call';
import { CallSessionStoreService } from './call-session-store.service';
import { CallZegoService } from './call-zego.service';

@Injectable({ providedIn: 'root' })
export class CallMediaStore {
  private readonly logger = createLogger('Call - CallMediaStore');

  private readonly zegoMediaControlService = inject(ZegoMediaControlService);
  private readonly zegoStreamService = inject(ZegoStreamService);
  private readonly localMediaPermissionService = inject(LocalMediaPermissionService);
  private readonly callZegoService = inject(CallZegoService);
  private readonly callSessionStore = inject(CallSessionStoreService);
  private readonly zimService = inject(ZimService);

  private lastSoundLevelUpdateTime = 0;
  private static readonly SOUND_LEVEL_THROTTLE_MS = 66;

  readonly isMicPermissionGranted = this.localMediaPermissionService.isPermissionMic;
  readonly isCamPermissionGranted = this.localMediaPermissionService.isPermissionCam;
  readonly hasCamDevice = this.localMediaPermissionService.hasCamDevice;
  readonly hasMicDevice = this.localMediaPermissionService.hasMicDevice;

  readonly hasCamAndPermission = computed(
    () => !!this.hasCamDevice() && this.isCamPermissionGranted(),
  );
  readonly hasMicAndPermission = computed(
    () => !!this.hasMicDevice() && this.isMicPermissionGranted(),
  );

  readonly localStream = computed(() => this.zegoStreamService.localStream()?.stream ?? null);
  readonly localStreamId = computed(() => this.zegoStreamService.localStream()?.streamID);
  readonly localMicState = computed(() => this.zegoMediaControlService.mediaState().isMicEnabled);
  readonly localCamState = computed(
    () => this.zegoMediaControlService.mediaState().isCameraEnabled,
  );
  readonly localSoundLevel = computed(() => this.zegoMediaControlService.mediaState().soundLevel);
  readonly isScreenSharing = computed(
    () => this.zegoMediaControlService.mediaState().isScreenSharing,
  );
  readonly isRemoteSharing = this.callZegoService.isRemoteSharing;
  readonly isAnyScreenSharing = computed(() => this.isScreenSharing() || this.isRemoteSharing());

  toggleMic(): void {
    const stream = this.localStream();
    if (stream) {
      const audioTrack = stream.getAudioTracks()[0];
      if (!audioTrack) {
        this.callZegoService.stopPubishAndDestroyStream(stream, this.localStreamId()!);
        const currentCall = this.callSessionStore.currentCall();
        if (currentCall) {
          this.publishCallStream(currentCall.callId, currentCall.type);
        }
        return;
      }
      this.zegoMediaControlService.toggleMicrophone();
      return;
    }

    const currentCall = this.callSessionStore.currentCall();
    if (currentCall) {
      this.publishCallStream(currentCall.callId, CALL_TYPE.AUDIO);
    }
  }

  async publishCallStream(callId: string, type: CallType): Promise<void> {
    this.logger.log('publishCallStream - start', { callId, type });

    if (!this.callSessionStore.isLoginRoom()) {
      this.logger.log('DEBUG-CALL', 'Room not logged in. Logging in...');
      const userInfo = getUserInfo();

      const isLoginRoom = await this.callZegoService.loginRoom(
        callId,
        this.zimService.userId ?? '',
        userInfo.username ?? '',
      );

      if (
        this.callSessionStore.callState() === CALL_STATE.ENDED ||
        this.callSessionStore.callState() === CALL_STATE.IDLE ||
        this.callSessionStore.currentCall()?.callId !== callId
      ) {
        if (isLoginRoom) {
          this.callZegoService.logoutRoom(callId);
        }
        this.logger.log('Call ended or rejected while waiting for loginRoom, aborting.');
        return;
      }

      this.callSessionStore.isLoginRoom.set(isLoginRoom);
      this.logger.log('DEBUG-CALL', 'Login Room Result:', isLoginRoom);
    } else {
      this.logger.log('DEBUG-CALL', 'Room already logged in.');
    }

    const shouldEnableCamera = type === CALL_TYPE.VIDEO && this.hasCamAndPermission();
    const shouldEnableMicrophone = this.hasMicAndPermission();

    this.logger.log('Publish Config:', {
      userId: this.zimService.userId,
      camera: shouldEnableCamera,
      microphone: shouldEnableMicrophone,
    });

    if (shouldEnableCamera || shouldEnableMicrophone) {
      this.logger.log('Creating & Publishing Local Stream...');
      await this.callZegoService.createAndPublishLocalStream(
        this.zimService.userId ?? '',
        shouldEnableCamera,
        shouldEnableMicrophone,
        type,
      );
      this.logger.log('Create & Publish Local Stream Success.');
    } else {
      this.logger.warn('Skip publish because no valid media device/permission.');
    }

    const stream = this.localStream();

    if (!stream) {
      this.logger.warn('Local Stream is NULL after publish.');
      return;
    }

    const audioTracks = stream.getAudioTracks();
    const videoTracks = stream.getVideoTracks();

    this.logger.log(
      'Audio Track Count:',
      audioTracks.length,
      'Video Track Count:',
      videoTracks.length,
    );

    stream.getTracks().forEach((track) => {
      this.logger.log('Track Info:', {
        kind: track.kind,
        label: track.label,
        readyState: track.readyState,
        muted: track.muted,
        enabled: track.enabled,
        settings: track.getSettings(),
      });

      if (track.muted) {
        this.logger.warn(`${track.kind} track is muted by browser/system.`);
      }

      if (track.readyState !== 'live') {
        this.logger.warn(`${track.kind} readyState=${track.readyState}`);
      }
    });

    const micTrack = audioTracks[0];
    const camTrack = videoTracks[0];

    const micState = !!micTrack?.enabled;
    const camState = !!camTrack?.enabled;

    this.logger.log('Final Mic State:', {
      exists: !!micTrack,
      enabled: micTrack?.enabled,
      muted: micTrack?.muted,
      readyState: micTrack?.readyState,
    });

    this.logger.log('Final Camera State:', {
      exists: !!camTrack,
      enabled: camTrack?.enabled,
      muted: camTrack?.muted,
      readyState: camTrack?.readyState,
    });

    this.zegoMediaControlService.setMediaState(micState, camState);

    this.logger.log('Media State Synced:', { micState, camState });
    this.logger.log('publishCallStream - end');
  }

  handleSoundLevelUpdate(soundLevelList: ZegoSoundLevelInfo[]): void {
    if (this.callSessionStore.callState() === CALL_STATE.IDLE) return;

    const now = Date.now();
    const throttled = now - this.lastSoundLevelUpdateTime < CallMediaStore.SOUND_LEVEL_THROTTLE_MS;

    for (const sl of soundLevelList) {
      if (sl.streamID.includes('screen')) continue;
      if (sl.streamID === this.localStreamId()) {
        if (!throttled) {
          this.zegoMediaControlService.updateLocalSoundLevel(sl.soundLevel);
        }
      } else if (!throttled) {
        let levels = this.callSessionStore.remoteSoundLevels();
        if (levels[sl.streamID] !== sl.soundLevel) {
          levels = { ...levels, [sl.streamID]: sl.soundLevel };
          this.callSessionStore.remoteSoundLevels.set(levels);
        }
      }
    }

    if (!throttled) {
      this.lastSoundLevelUpdateTime = now;
    }
  }

  handleRemoteMicStatusUpdate(streamID: string, status: 'OPEN' | 'MUTE'): void {
    this.logger.log('Remote Mic Status Update:', { streamID, status });
    if (streamID.includes('screen')) return;
    const participants = this.callSessionStore.participants();
    const mapParticipants = participants.map((p) => {
      if (p.userId !== streamID) return p;
      return { ...p, isMicOn: status === 'OPEN' };
    });
    this.callSessionStore.setParticipants(mapParticipants);
  }

  handleRemoteCameraStatusUpdate(streamID: string, status: 'OPEN' | 'MUTE'): void {
    this.logger.log('Remote Camera Status Update:', { streamID, status });
    if (streamID.includes('screen')) return;
    if (this.callSessionStore.currentCall()?.type === CALL_TYPE.AUDIO && status === 'OPEN') {
      this.callSessionStore.updateCurrentCall((call) =>
        call ? { ...call, type: CALL_TYPE.VIDEO } : null,
      );
    }
    const participants = this.callSessionStore.participants();
    const mapParticipants = participants.map((p) => {
      if (p.userId !== streamID) return p;
      return { ...p, isCamOn: status === 'OPEN' };
    });
    this.callSessionStore.setParticipants(mapParticipants);
  }
}
